/**
 * Ad-zone policy — the anti-cannibalization guardrail for running Google AdSense
 * ALONGSIDE the first-party monetization (affiliate / employer-paid listings /
 * Pro). It is legal + AdSense-policy-compliant to run both, but only profitable
 * if AdSense lives in LOW-INTENT zones (informational readers we weren't going to
 * convert) and never on HIGH-INTENT conversion surfaces (job detail, apply, feed,
 * category) where it would:
 *   1. skim clicks off-site for low RPM instead of converting to affiliate /
 *      employer / Pro (cannibalization), and
 *   2. sit next to "Apply" / affiliate CTAs → AdSense deceptive-placement risk.
 *
 * Each `AdSlot` slot is bound to a known page type, so the zone is intrinsic to
 * the slot name — no per-call decision, no way to mis-place an ad.
 *
 * SUPERSEDED 2026-09-21: the anti-cannibalization invariant that used to sit
 * here (AdSense only on informational slots) was lifted by owner directive —
 * see `isAdsenseEligibleSlot`. Zones remain as a telemetry split.
 */

export type SlotName =
	| "blog-top" // After 1st paragraph in blog post — INFORMATIONAL
	| "blog-bottom" // After last paragraph in blog post — INFORMATIONAL
	| "feed-sidebar" // Desktop sidebar on the job feed — CONVERSION
	| "category-footer" // Bottom of category pages — CONVERSION
	| "job-detail-bottom" // Below job content on detail page — CONVERSION
	| "job-detail-mid" // Inside job content, after Requirements — CONVERSION
	| "feed-inline-display"; // Labelled unit inside the /jobs list — CONVERSION

export type AdZone = "informational" | "conversion";

/**
 * Slot → zone. Blog (editorial readers, low purchase/apply intent) is the only
 * AdSense-eligible surface. Everything tied to the apply/convert funnel is
 * `conversion` and must NOT show third-party ads.
 */
export const AD_ZONE: Record<SlotName, AdZone> = {
	"blog-top": "informational",
	"blog-bottom": "informational",
	"feed-sidebar": "conversion",
	"category-footer": "conversion",
	"job-detail-bottom": "conversion",
	"job-detail-mid": "conversion",
	"feed-inline-display": "conversion",
};

/**
 * Content-quality signals for the CURRENT page/screen, supplied by the caller
 * from whatever indexability/result-count computation it already has (job
 * pages: `isJobIndexable`; search/facet pages: their result `total`). Both
 * optional — a signal that isn't supplied is treated as "fine" (`true`), so
 * this gate is strictly additive and never breaks a caller that hasn't wired
 * a content check yet.
 */
export interface AdContentSignals {
	/** False on a noindexed/thin page. */
	indexable?: boolean;
	/** False on a search/facet page with zero results. */
	hasResults?: boolean;
	/**
	 * True on an employer-paid sponsored post. Third-party networks must never
	 * render there (a competitor's ad on paid content); the sponsor bought the
	 * page. Applies to every network, not just AdSense.
	 */
	sponsored?: boolean;
}

/**
 * True where real third-party AdSense is allowed to render.
 *
 * POLICY CHANGE 2026-09-21 (owner directive): every slot is AdSense-eligible,
 * conversion zones included. The header invariant above described the earlier
 * anti-cannibalization stance (AdSense only on informational blog slots); with
 * the blog zone rendering no loader, that stance meant AdSense could not appear
 * anywhere, so the account earned a structural zero. `AD_ZONE` is kept as a
 * telemetry classification (`adZoneOf`) so informational vs conversion RPM can
 * still be split, which is how the cannibalization cost gets measured.
 *
 * What still holds: when supplied, the page's own content-quality signals must
 * both hold. Google's AdSense ad-placement policy forbids ads on "screens
 * without publisher content" — a noindexed page, or a search/facet page with
 * zero results, must never show a real ad. (`AdSlot` also returns null for
 * those before this is consulted.)
 */
export function isAdsenseEligibleSlot(
	_slot: SlotName,
	signals?: AdContentSignals,
): boolean {
	// Employer-paid content: no third-party ad, whatever the zone policy says.
	if (signals?.sponsored === true) return false;
	if (signals?.indexable === false) return false;
	if (signals?.hasResults === false) return false;
	return true;
}

/**
 * Slots where the Adsterra Native Banner may render.
 *
 * This is a SEPARATE, deliberately narrower list from `isAdsenseEligibleSlot`,
 * not a widening of it — the AdSense zone invariant above is unchanged.
 *
 * Why a second list exists at all: the two AdSense-eligible slots
 * (`blog-top` / `blog-bottom`) are rendered by nothing. The blog lives in a
 * different deployable (`apps/content/`) which does not import `AdSlot`, so
 * "informational only" resolves to "nowhere", which is the structural reason
 * ad impressions have run 100% `surface=placeholder` since the component
 * shipped. A network that can only serve on pages that do not exist is not a
 * revenue rail.
 *
 * Why `job-detail-bottom` specifically, and nothing else:
 *   - it renders BELOW the job content and BELOW the apply CTA, so the reader
 *     has already passed the conversion point before the unit is reachable —
 *     which is the cannibalization concern the zone policy was written for;
 *   - `feed-sidebar` is the inventory the `/advertise` rate card sells
 *     (Rp 750k / $69 a month). Filling it with a network unit at a fraction of
 *     that price would undercut a product we are actively selling;
 *   - `category-footer` is mid-funnel — the reader is still choosing a job.
 *
 * The `indexable` / `hasResults` gates still apply: a noindexed or empty page
 * must never carry a third-party ad, whichever network it comes from.
 */
/**
 * DISABLED 2026-09-29 (brand safety / legal). An audit of Adsterra's Native
 * Banner feed (300 ads over 15 pulls) was 34% online gambling (Indonesian
 * slots in every pull; online gambling is illegal in Indonesia, our main
 * audience), 24% dating/adult-adjacent, 23% piracy and 8% fake security
 * alerts. Evidence: scripts/research/adsterra-smartlink-2026-09/README.md.
 *
 * The empty list means `isAdsterraEligibleSlot` is false for every slot, so
 * `AdSlot` passes `adsterraUnit=null` and the affiliate fallback fills instead.
 * Re-enable ONLY when Adsterra confirms category exclusions AND a fresh
 * 15-pull feed audit comes back clean. The slot list this replaced was
 * ["blog-top", "blog-bottom", "job-detail-bottom"].
 */
export const ADSTERRA_ELIGIBLE_SLOTS: readonly SlotName[] = [];

export function isAdsterraEligibleSlot(
	slot: SlotName,
	signals?: AdContentSignals,
): boolean {
	if (!ADSTERRA_ELIGIBLE_SLOTS.includes(slot)) return false;
	if (signals?.sponsored === true) return false;
	if (signals?.indexable === false) return false;
	if (signals?.hasResults === false) return false;
	return true;
}

/**
 * Zone for a slot name. Returns `undefined` for an unknown slot so callers can
 * default telemetry without a thrown index — AdSlotClient receives `slot` as a
 * plain string (it is server-driven and not statically bound to SlotName), so
 * this guards the lookup. Used to tag ad telemetry with the anti-cannibalization
 * zone so informational vs conversion ad performance can be split in PostHog.
 */
export function adZoneOf(slot: string): AdZone | undefined {
	return AD_ZONE[slot as SlotName];
}
